import { Users, Sparkles, ArrowRight, Mail, MapPin, Instagram, Facebook, Youtube, Music2 } from "lucide-react";
import { useCMS, usePage, useStat, useSection } from "@/hooks/useCMS";
import { useLang } from "@/context/LanguageContext";
import { Eyebrow, StaggeredHeading, PillButton, ArrowImage, Reveal, TiltCard, QuoteRotator } from "@/components/motifs";
import { CmsSection } from "@/components/CmsSection";
import { TemplateSections } from "@/components/TemplateSections";

const T = ["lg:-rotate-2", "lg:rotate-1", "lg:-rotate-1", "lg:rotate-2"];

export default function About() {
  const { data } = useCMS();
  const { t } = useLang();
  const page = usePage("about");
  const secBoard = useSection("ab_board");
  const timeline = data.timeline || [];
  const partners = data.partners || [];
  const board = data.board || [];
  const quote = (data.quotes || [])[0] || {};
  const gallery = data.gallery || [];
  const s = data.settings || {};
  const stats = [
    { v: useStat("communities") || "500+", l: "communities" },
    { v: useStat("interactions") || "50M+", l: "interactions" },
    { v: useStat("partners") || "20+", l: "partners" },
  ];

  return (
    <div data-testid="about-page" className="flex flex-col">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pt-14 pb-10 lg:pt-20">
        <Eyebrow icon={Users}>{page.eyebrow || "The coalition"}</Eyebrow>
        <div className="mt-5 max-w-3xl">
          <StaggeredHeading animate lines={[
            { text: page.headline_1 || "The coalition" },
            { text: page.headline_2 || "that moves", emphasis: true, accent: "#DB1A5C" },
            { text: page.headline_3 || "Canada." },
          ]} />
        </div>
        <p className="mt-7 max-w-2xl text-lg text-muted-foreground">{page.intro}</p>
        <div className="mt-8 flex flex-wrap gap-4">
          <PillButton to="/Initiatives" icon={ArrowRight} testid="about-initiatives">{page.cta_primary_label || "See our initiatives"}</PillButton>
          <PillButton href="#contact" variant="ghost" testid="about-contact">{page.cta_secondary_label || "Get in touch"}</PillButton>
        </div>
      </section>

      {/* 2030 MISSION BAND */}
      <CmsSection sk="ab_mission">
      <section className="bg-[#2CB6C2]/10 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <Reveal>
            <h2 className="font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95] max-w-3xl">
              The healthiest nation on earth. <span className="italic text-[#2CACD4]">Measured in moves, not medals.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            {stats.map((st) => (
              <Reveal key={st.l}>
                <p className="font-display font-black italic text-5xl sm:text-6xl text-[#DB1A5C]">{st.v}</p>
                <p className="mt-2 text-sm uppercase tracking-wider text-muted-foreground">{st.l}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      </CmsSection>
      {/* LEADER */}
      <CmsSection sk="ab_leader">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <div className="relative mx-auto max-w-md">
            <img src={gallery[1]?.url} alt="Foundation leader on the ground" className="w-full rounded-3xl object-cover shadow-xl lg:-rotate-2" />
            <span className="absolute -bottom-4 left-6 rounded-full bg-[#DB1A5C] px-4 py-2 text-xs font-black uppercase text-white shadow-lg">On the ground, every week</span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Sparkles className="text-[#2CACD4]" />
          <div className="mt-4"><QuoteRotator quotes={data.quotes} align="left" /></div>
          <p className="mt-6 text-muted-foreground">Behind the campaign is a coalition of clinicians, educators, athletes and volunteers who believe movement belongs to everyone — in every season, in every community.</p>
        </Reveal>
      </section>

      </CmsSection>
      {/* MEDICAL ADVISORY BOARD */}
      <CmsSection sk="ab_board">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24">
        <Reveal>
          <Eyebrow icon={Sparkles}>{secBoard.eyebrow || t("Medical Advisory Board", "Comité consultatif médical")}</Eyebrow>
          <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95] max-w-3xl">
            {t("Guided by", "Guidé par")} <span className="italic text-[#DB1A5C]">{t("the world's best.", "les meilleurs au monde.")}</span>
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">{t("A holistic, evidence-based team of 100+ experts across a dozen disciplines — united by a passion for moving the needle on mental health.", "Une équipe holistique et fondée sur des données probantes de 100+ experts dans une douzaine de disciplines — unis par la passion de faire progresser la santé mentale.")}</p>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {board.map((m, i) => (
            <Reveal key={m.id || i} delay={(i % 3) * 0.08}>
              <article className="group flex h-full flex-col rounded-3xl border border-border bg-card p-6 hover:shadow-xl transition-shadow" data-testid={`board-member-${i}`}>
                <img src={m.image} alt={m.image_alt || m.name} className="h-20 w-20 rounded-2xl object-cover shadow-sm" style={{ borderBottom: `4px solid ${m.accent || "#2CACD4"}` }} />
                <h3 className="mt-4 font-display font-black uppercase text-lg leading-tight">{m.name}</h3>
                <p className="text-sm font-bold uppercase tracking-wider" style={{ color: m.accent || "#2CACD4" }}>{m.role}</p>
                <p className="mt-3 text-sm text-muted-foreground flex-1">{m.bio}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      </CmsSection>
      {/* TIMELINE */}
      <CmsSection sk="ab_timeline">
      <section className="bg-[#2CB6C2]/10 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <Reveal>
            <Eyebrow icon={Sparkles}>How we got here</Eyebrow>
            <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-4xl">A decade in motion</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {timeline.map((t, i) => (
              <Reveal key={t.id || i} delay={(i % 4) * 0.08}>
                <TiltCard accent={t.accent} tilt={T[i % 4]} className="p-6 h-full" testid={`timeline-${i}`}>
                  <p className="font-display font-black italic text-4xl" style={{ color: t.accent }}>{t.year}</p>
                  <h3 className="mt-3 font-display font-black uppercase text-lg leading-tight">{t.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{t.blurb}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      </CmsSection>
      {/* PARTNER CHIPS */}
      <CmsSection sk="ab_partners">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-20">
        <Reveal>
          <Eyebrow icon={Users}>Our partners</Eyebrow>
          <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-4xl">Non-profits, for-profits & everyone in between.</h2>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-3">
          {partners.map((p) => (
            <span key={p.id || p.name} className="rounded-full bg-card border border-border px-5 py-3 font-display font-bold uppercase text-sm hover:border-[#2CACD4] hover:text-[#2CACD4] transition-colors cursor-default">{p.name}</span>
          ))}
        </div>
      </section>

      </CmsSection>
      {/* CONTACT */}
      <CmsSection sk="ab_contact">
      <section id="contact" className="bg-[#0F172A] text-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Eyebrow icon={Mail} color="#CAE295">Say hello</Eyebrow>
            <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
              Bring the movement <span className="italic text-[#2CACD4]">to your community.</span>
            </h2>
            <div className="mt-7 space-y-3 text-white/80">
              <a href={`mailto:${s.contact_email || "info@showusyourmoves.ca"}`} className="flex items-center gap-3 hover:text-white cursor-pointer"><Mail size={18} /> {s.contact_email || "info@showusyourmoves.ca"}</a>
              <p className="flex items-center gap-3"><MapPin size={18} /> {s.location || "Ottawa, ON · Canada"}</p>
            </div>
            <div className="mt-6 flex gap-3">
              {[[Instagram, s.social_instagram], [Facebook, s.social_facebook], [Youtube, s.social_youtube], [Music2, s.social_tiktok]].map(([Icon, href], i) => (
                <a key={i} href={href || "#"} target="_blank" rel="noopener noreferrer" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 hover:bg-[#2CACD4] hover:text-slate-950 transition-colors cursor-pointer" aria-label="Social link">
                  <Icon size={19} />
                </a>
              ))}
            </div>
          </div>

          <div className="rounded-3xl bg-white/[0.06] border border-white/15 p-8 backdrop-blur">
            <h3 className="font-display font-black italic uppercase text-2xl">Join the movement. It's free.</h3>
            <p className="mt-2 text-white/70">Find a move near you, or add your own to the national map.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <PillButton to="/Events" variant="dark" testid="contact-find-event">Find an event</PillButton>
              <PillButton to="/Home#list" variant="pink" testid="contact-list-event">List an event</PillButton>
            </div>
          </div>
        </div>
      </section>
      </CmsSection>
      <TemplateSections page="about" />
    </div>
  );
}
